import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { Pencil, Save, X, FileText, Loader2 } from 'lucide-react'

interface BookingNotesSectionProps {
  bookingId: string
  initialNotes: string | null
}

export function BookingNotesSection({
  bookingId,
  initialNotes,
}: BookingNotesSectionProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [isEditing, setIsEditing] = useState(false)
  const [notes, setNotes] = useState(initialNotes || '')

  const saveMutation = useMutation({
    mutationFn: async (newNotes: string) => {
      const { error } = await supabase
        .from('bookings')
        .update({ internal_notes: newNotes || null })
        .eq('id', bookingId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-detail', bookingId] })
      toast({
        title: 'Notes saved',
        description: 'Internal notes have been updated.',
      })
      setIsEditing(false)
    },
    onError: (error) => {
      toast({
        title: 'Error saving notes',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleSave = () => {
    saveMutation.mutate(notes)
  }

  const handleCancel = () => {
    setNotes(initialNotes || '')
    setIsEditing(false)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-medium">
          <FileText className="h-4 w-4" />
          Internal Notes
        </CardTitle>
        {!isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="h-8"
          >
            <Pencil className="h-3 w-3 mr-1" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes about this booking..."
              rows={4}
              className="resize-none"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={saveMutation.isPending}
              >
                <X className="h-3 w-3 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-3 w-3 mr-1" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {initialNotes || 'No internal notes added yet.'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
